import { useCallback, useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ErrorLogItem } from "@/features/errorLogs/ErrorLogItem";
import { fetchErrorLogs, refreshErrorLogs, type ErrorLogEntry } from "@/lib/errorLogsApi";

interface ErrorLogsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ErrorLogsDialog({ open, onClose }: ErrorLogsDialogProps) {
  const [errors, setErrors] = useState<ErrorLogEntry[]>([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      if (force) {
        setRefreshing(true);
        await refreshErrorLogs();
      }
      const data = await fetchErrorLogs();
      setErrors(data.errors);
      setAiEnabled(data.ai_enabled);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehlerliste konnte nicht geladen werden.");
      setErrors([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Server-Fehler (App-Admin)"
      description={
        errors.length > 0
          ? `${errors.length} offene Fehler · ${aiEnabled ? "KI-Analyse aktiv" : "KI-Analyse deaktiviert"}`
          : aiEnabled
            ? "KI-Analyse aktiv"
            : "KI-Analyse deaktiviert (kein OPENAI_API_KEY)"
      }
      size="3xl"
      fullScreenMobile
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <div className="mb-4 flex justify-end">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={refreshing}
          onClick={() => void load(true)}
        >
          {refreshing ? "Lädt …" : "Neu laden"}
        </Button>
      </div>

      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {loading && errors.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-label-secondary">
          <Spinner />
          Fehler werden geladen …
        </div>
      ) : errors.length === 0 ? (
        <Alert variant="success">Keine offenen Fehler – alles ruhig.</Alert>
      ) : (
        <div className="space-y-4">
          {errors.map((entry) => (
            <ErrorLogItem
              key={entry.fingerprint}
              error={entry}
              aiEnabled={aiEnabled}
              onChanged={() => void load()}
            />
          ))}
        </div>
      )}
    </Dialog>
  );
}
